import DOMPurify from 'dompurify'
import type {
  ImpactArea,
  PlaceholderMap,
  StatementForDisability,
  buildIODStatementParams,
  buildTemplateIODStatementParams
} from './impact-of-disability_types'
import { getNonAIIODSubjects } from './subjects'

export function populatePlaceholderText(text: string, placeholders: PlaceholderMap): string {
  let populatedText = text
  Object.entries(placeholders).forEach(([key, value]) => {
    if (value !== undefined) {
      const regex = new RegExp(`\\[${key}\\]`, 'g')
      populatedText = populatedText.replace(regex, value)
    }
  })
  return populatedText
}

/**
 * Statement builder that works directly with LLM-generated
 * statements instead of Airtable impact area intros.
 *
 * Expects statementsForDisability to be an array of objects with:
 * { subject, area_uuid, statement_text }
 */
export function buildIODStatement({
  statementsForDisability,
  selectedSubjects,
  selectedImpactAreas
}: buildIODStatementParams): [string, boolean] {
  const hasSelectedSubjects = selectedSubjects && selectedSubjects.length > 0
  const hasSelectedImpactAreas = selectedImpactAreas && selectedImpactAreas.length > 0

  let statement = ''
  let isStatementEmptyPlaceholder = false

  if (!hasSelectedImpactAreas) {
    const hasLLMStatements = (statementsForDisability || []).length > 0

    // If there are no impact areas configured but we DO have statements for the selected subjects
    if (hasSelectedSubjects && hasLLMStatements) {
      const filteredBySubject = statementsForDisability.filter(s =>
        selectedSubjects.includes(s.subject)
      )

      const statements = filteredBySubject.map(s => s.statement_text)
      statement = statements.map(s => `<p>${DOMPurify.sanitize(s)}</p>`).join('')
      return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
    }

    if (hasSelectedSubjects && !hasLLMStatements) {
      statement = '[Select an impacted area to compose a statement.]'
    } else if (!hasSelectedSubjects) {
      statement = '[Select a subject and an impacted area to compose a statement.]'
    }
    isStatementEmptyPlaceholder = true
    return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
  }

  if (!hasSelectedSubjects) {
    statement = '[Select a subject and an impacted area to compose a statement.]'
    isStatementEmptyPlaceholder = true
    return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
  }

  const selectedAreaUUIDs = selectedImpactAreas.map(ia => ia.area_uuid)

  const filteredStatements = (statementsForDisability || []).filter(s => {
    return selectedSubjects.includes(s.subject) && selectedAreaUUIDs.includes(s.area_uuid)
  })

  const statements = filteredStatements.map(s => s.statement_text)
  statement = statements.map(s => `<p>${DOMPurify.sanitize(s)}</p>`).join('')

  return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
}

const SELECT_SUBJECT_AND_IMPACT_AREA_PROMPT = '[Select a subject and an impacted area to compose a statement.]'
const SELECT_IMPACT_AREA_PROMPT = '[Select an impacted area to compose a statement.]'
const SELECT_SUBJECT_TO_COMPLETE_PROMPT = '[Select a subject to complete the statement.]'
const STUDENT_PLACEHOLDERS = /\{student\}|\{pronoun\}/g
const STUDENT_PLACEHOLDER_TEXT = '(student)'

function emptyTemplateStatementPrompt(hasSelectedSubjects: boolean): string {
  return hasSelectedSubjects ? SELECT_IMPACT_AREA_PROMPT : SELECT_SUBJECT_AND_IMPACT_AREA_PROMPT
}

function byAreaOrder(a: ImpactArea, b: ImpactArea): number {
  return parseInt(a.area_order) - parseInt(b.area_order)
}

function bySubjectOrder(a: StatementForDisability, b: StatementForDisability): number {
  const subjectOrder = getNonAIIODSubjects().map(subject => subject.value)
  return subjectOrder.indexOf(a.subject) - subjectOrder.indexOf(b.subject)
}

function impactAreaParagraphs(
  impactArea: ImpactArea,
  statementsForDisability: StatementForDisability[],
  selectedSubjects: string[]
): string[] {
  const introduction = impactArea.area_statement_text || ''
  if (selectedSubjects.length === 0) {
    return [introduction, SELECT_SUBJECT_TO_COMPLETE_PROMPT]
  }

  const subjectSentences = statementsForDisability
    .filter(s => s.area_uuid === impactArea.area_uuid && selectedSubjects.includes(s.subject))
    .sort(bySubjectOrder)
    .map(s => s.statement_text)
  return [[introduction, ...subjectSentences].join(' ')]
}

function toParagraphHtml(paragraph: string): string {
  const withStudentPlaceholder = paragraph.replace(STUDENT_PLACEHOLDERS, STUDENT_PLACEHOLDER_TEXT)
  return `<p>${DOMPurify.sanitize(withStudentPlaceholder)}</p>`
}

// A disability with a single impact area composes it without the user selecting it.
export function buildTemplateIODStatement({
  impactAreaOptions,
  statementsForDisability,
  selectedSubjects,
  selectedImpactAreas
}: buildTemplateIODStatementParams): [string, boolean] {
  const hasSingleImpactArea = impactAreaOptions.length === 1
  const impactAreasToCompose = hasSingleImpactArea ? impactAreaOptions : selectedImpactAreas

  if (impactAreasToCompose.length === 0) {
    return [emptyTemplateStatementPrompt(selectedSubjects.length > 0), true]
  }

  const statement = [...impactAreasToCompose]
    .sort(byAreaOrder)
    .flatMap(impactArea => impactAreaParagraphs(impactArea, statementsForDisability, selectedSubjects))
    .map(toParagraphHtml)
    .join('')
  return [statement, false]
}
